import { useToast } from "@chakra-ui/react";
import { useContext } from "react";
import { useNavigate } from "react-router";

import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { CheckboxGroup, Select, Text } from "@equidefi/ui";
import RadioGroup from "@equidefi/ui/forms/RadioGroup";

import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSaveVaultAdditionalInfo } from "@equidefi/portals/hooks/useVault";
import {
  associationsOptions,
  employmentStatusOptions,
  investmentObjectiveOptions,
  maritalStatusOptions,
} from "@equidefi/shared/constants/accreditation";

const additionalInfoSchema = z.object({
  marital_status: z.string(),
  employment_status: z.string(),
  associations: z.array(z.string()).optional(),
  investment_objective: z.string().refine((val) => val === "Speculation", {
    message: 'You must select "Speculation" as your investment objective',
  }),
});

type AdditionalInfoFormData = z.infer<typeof additionalInfoSchema>;

export const AdditionalInfoForm = () => {
  const { investment, analytics, offering } = useWorkflowContext();
  const { vault } = useContext(VaultContext);
  const navigate = useNavigate();
  useDocumentTitle(["Accreditation", offering?.name, investment?.issuer?.name]);

  const saveVaultInfo = useSaveVaultAdditionalInfo(investment?.id as string);

  const toast = useToast();

  const navigateToVault = () => {
    navigate(`/vault/dashboard`, {
      state: {
        completed: true,
        isPaid:
          Number(
            investment?.payments?.filter((p) => p.status === "succeeded")
              ?.length,
          ) > 0,
        investment_id: investment?.id,
      },
    });
  };

  const onSubmit = async (values: AdditionalInfoFormData) => {
    try {
      await saveVaultInfo.mutateAsync({
        marital_status: {
          label: "Maritial Status",
          value: values.marital_status,
        },
        employment_status: {
          label: "Employment Status",
          value: values.employment_status,
        },
        associations: {
          label: "Associations",
          value: values.associations,
        },
        investment_objective: {
          label: "Investment Objectives",
          value: values.investment_objective,
        },
      });
      analytics.track("complete");
      navigateToVault();
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: "Failed to submit",
      });
    }
  };

  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
  } = useForm<AdditionalInfoFormData>({
    defaultValues: {
      marital_status: vault?.additional_info?.marital_status?.value,
      employment_status: vault?.additional_info?.employment_status?.value,
      associations: vault?.additional_info?.associations?.value,
      investment_objective: vault?.additional_info?.investment_objective?.value,
    },
    resolver: zodResolver(additionalInfoSchema),
  });

  return (
    <WorkflowContainer
      title="Additional Info"
      onContinue={handleSubmit(onSubmit)}
      buttonLabel="Finish Investment"
      buttonProps={{
        isDisabled: !isValid,
      }}>
      <WorkflowForm onSubmit={handleSubmit(onSubmit)}>
        <Text fontWeight="bold" textStyle="h2" mb="2" mt={5}>
          Marital status
        </Text>
        <Controller
          name="marital_status"
          control={control}
          render={({ field }) => (
            <RadioGroup
              fieldName="marital_status"
              defaultValue={field.value}
              options={maritalStatusOptions}
              errors={errors}
              setFieldValue={(_, value) => {
                field.onChange(value);
              }}
              mb="4"
              wrap="wrap"
              direction="row"
              elementWidth="32%"
            />
          )}
        />

        <Text fontWeight="bold" textStyle="h2" mb="2">
          Employment status
        </Text>
        <Controller
          name="employment_status"
          control={control}
          render={({ field }) => {
            return (
              <RadioGroup
                fieldName="employment_status"
                defaultValue={field.value}
                options={employmentStatusOptions}
                errors={errors}
                setFieldValue={(_, value) => {
                  field.onChange(value);
                }}
                mb="4"
                wrap="wrap"
                direction="row"
                elementWidth="32%"
              />
            );
          }}
        />

        <Text fontWeight="bold" textStyle="h2" mb="0" mt={10}>
          Associations
        </Text>
        <Text mb="2.5">Select all that apply</Text>
        <Controller
          name="associations"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <CheckboxGroup
                defaultValue={field.value}
                options={associationsOptions}
                onChange={field.onChange}
                error={fieldState.error?.message}
                mb="3"
              />
            );
          }}
        />

        <Text fontWeight="bold" textStyle="h2" mb="0" mt={10}>
          Investment Objectives
        </Text>
        <Text mb="0">
          This investment is only available to investors whose objective is
          Speculation.
        </Text>
        <Text>Select one</Text>
        <Controller
          name="investment_objective"
          control={control}
          render={({ field, fieldState }) => (
            <RadioGroup
              fieldName="investment_objective"
              defaultValue={field.value}
              options={investmentObjectiveOptions}
              errors={errors}
              setFieldValue={(_, value) => {
                field.onChange(value);
              }}
              mb="4"
              elementWidth="32%"
            />
          )}
        />
      </WorkflowForm>
    </WorkflowContainer>
  );
};
